import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { leaveAPI, bulkAPI } from '../services/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  CalendarOff, Plus, Check, X, Clock, Calendar, Loader2, RefreshCw, CheckSquare, CalendarDays, CalendarCheck, Briefcase, Users, Search
} from 'lucide-react';
import toast from 'react-hot-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Leaves = () => {
  const { user, isAdmin, isTeamLead } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [newLeave, setNewLeave] = useState({
    type: 'Casual Leave', from_date: '', to_date: '', reason: ''
  });

  // Admin: All employee leave balances
  const [allLeaveBalances, setAllLeaveBalances] = useState([]);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [balanceYear, setBalanceYear] = useState(new Date().getFullYear());
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'requests'

  const years = [2024, 2025, 2026];

  useEffect(() => {
    loadLeaves();
    loadLeaveBalance();
    if (isAdmin) loadAllLeaveBalances();
  }, [user?.id]);

  useEffect(() => {
    loadLeaveBalance();
  }, [selectedYear]);

  const loadLeaveBalance = useCallback(async () => {
    if (!user?.id || isAdmin) return;
    try {
      const balance = await leaveAPI.getBalance(user.id, selectedYear);
      setLeaveBalance(balance);
    } catch (error) {
      console.error('Error loading leave balance:', error);
    }
  }, [user?.id, selectedYear, isAdmin]);

  const loadAllLeaveBalances = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingBalances(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/all-leave-balances?year=${balanceYear}`);
      if (!response.ok) throw new Error('Failed to load');
      const data = await response.json();
      setAllLeaveBalances(data || []);
    } catch (error) {
      console.error('Error loading leave balances:', error);
      toast.error('Failed to load employee leave balances');
    } finally {
      setLoadingBalances(false);
    }
  }, [isAdmin, balanceYear]);

  useEffect(() => {
    if (isAdmin) loadAllLeaveBalances();
  }, [balanceYear, loadAllLeaveBalances]);

  const loadLeaves = useCallback(async (isRefresh = false) => {
    if (!user?.id) return;
    
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    
    try {
      // For admin: get all leaves (no filter)
      // For team lead: get only their team's leaves
      // For employee: get only their own leaves
      let empId = null;
      let teamLeadId = null;
      
      if (isAdmin) {
        // Admin sees all
        empId = null;
        teamLeadId = null;
      } else if (isTeamLead) {
        // Team Lead sees only their team's leaves
        empId = null;
        teamLeadId = user.id;
      } else {
        // Employee sees only their own
        empId = user.id;
      }
      
      const data = await leaveAPI.getAll(empId, null, teamLeadId);
      setLeaves(data || []);
      setSelectedIds([]);
      if (isRefresh) {
        toast.success('Refreshed!');
        loadLeaveBalance();
      }
    } catch (error) {
      console.error('Error loading leaves:', error);
      toast.error('Failed to load leave requests');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.id, isAdmin, isTeamLead, loadLeaveBalance]);

  const filteredLeaves = leaves.filter(leave => {
    const matchesStatus = filterStatus === 'all' || leave.status === filterStatus;
    const matchesUser = isAdmin || isTeamLead || leave.emp_id === user?.id;
    return matchesStatus && matchesUser;
  });

  const pendingCount = filteredLeaves.filter(l => l.status === 'pending').length;
  const approvedCount = leaves.filter(l => l.status === 'approved').length;
  const rejectedCount = leaves.filter(l => l.status === 'rejected').length;
  const totalPendingCount = leaves.filter(l => l.status === 'pending').length;

  const handleAddLeave = async () => {
    if (!newLeave.from_date || !newLeave.to_date || !newLeave.reason) {
      toast.error('Please fill all required fields');
      return;
    }

    const from = new Date(newLeave.from_date);
    const to = new Date(newLeave.to_date);
    
    if (to < from) {
      toast.error('End date cannot be before start date');
      return;
    }

    const days = Math.ceil((to - from) / (1000 * 60 * 60 * 24)) + 1;
    
    setSubmitting(true);
    try {
      await leaveAPI.create({
        emp_id: user.id,
        emp_name: user.name,
        type: newLeave.type,
        from_date: newLeave.from_date,
        to_date: newLeave.to_date,
        days,
        reason: newLeave.reason
      });
      toast.success('Leave request submitted successfully!');
      setNewLeave({ type: 'Casual Leave', from_date: '', to_date: '', reason: '' });
      setIsAddDialogOpen(false);
      await loadLeaves();
    } catch (error) {
      toast.error(error.message || 'Failed to submit leave request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await leaveAPI.approve(id, user.id);
      toast.success('Leave approved!');
      await loadLeaves();
    } catch (error) {
      toast.error(error.message || 'Failed to approve');
    }
  };

  const handleReject = async (id) => {
    try {
      await leaveAPI.reject(id, user.id);
      toast.success('Leave rejected');
      await loadLeaves();
    } catch (error) {
      toast.error(error.message || 'Failed to reject');
    }
  };

  const handleSelectAll = () => {
    const pendingIds = filteredLeaves.filter(l => l.status === 'pending').map(l => l.id);
    setSelectedIds(selectedIds.length === pendingIds.length ? [] : pendingIds);
  };

  const handleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleBulkApprove = async () => {
    if (selectedIds.length === 0) return;
    setBulkProcessing(true);
    try {
      const result = await bulkAPI.approveLeaves(selectedIds, user.id);
      toast.success(`${result.count} leaves approved!`);
      setSelectedIds([]);
      await loadLeaves();
    } catch (error) {
      toast.error(error.message || 'Failed to approve');
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedIds.length === 0) return;
    setBulkProcessing(true);
    try {
      const result = await bulkAPI.rejectLeaves(selectedIds, user.id);
      toast.success(`${result.count} leaves rejected!`);
      setSelectedIds([]);
      await loadLeaves();
    } catch (error) {
      toast.error(error.message || 'Failed to reject');
    } finally {
      setBulkProcessing(false);
    }
  };

  const filteredBalances = allLeaveBalances.filter(emp => 
    emp.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    emp.emp_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E2A5E]" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Leave Management</h1>
          <p className="text-sm text-gray-500">{(isAdmin || isTeamLead) ? 'Manage employee leaves' : 'Apply for leave'}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-9 w-9 sm:h-10 sm:w-10" onClick={() => loadLeaves(true)} disabled={refreshing}>
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          </Button>
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#1E2A5E] hover:bg-[#2D3A8C] h-9 sm:h-10 text-sm sm:text-base px-3 sm:px-4">
                <Plus size={16} className="mr-1 sm:mr-2" /> Apply Leave
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Apply for Leave</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Leave Type</Label>
                  <Select value={newLeave.type} onValueChange={(v) => setNewLeave({...newLeave, type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Casual Leave">Casual Leave</SelectItem>
                      <SelectItem value="Sick Leave">Sick Leave</SelectItem>
                      <SelectItem value="Vacation">Vacation</SelectItem>
                      <SelectItem value="Personal">Personal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>From Date</Label>
                    <Input type="date" value={newLeave.from_date} onChange={(e) => setNewLeave({...newLeave, from_date: e.target.value})} />
                  </div>
                  <div className="space-y-2">
                    <Label>To Date</Label>
                    <Input type="date" value={newLeave.to_date} onChange={(e) => setNewLeave({...newLeave, to_date: e.target.value})} />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Reason</Label>
                  <Textarea placeholder="Enter reason" value={newLeave.reason} onChange={(e) => setNewLeave({...newLeave, reason: e.target.value})} />
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild><Button variant="outline">Cancel</Button></DialogClose>
                <Button onClick={handleAddLeave} className="bg-[#1E2A5E] hover:bg-[#2D3A8C]" disabled={submitting}>
                  {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Submit
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats - Admin/TL only */}
      {(isAdmin || isTeamLead) && (
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <Card>
            <CardContent className="p-2 sm:p-4 flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-yellow-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                <Clock size={16} className="sm:hidden text-yellow-600" />
                <Clock size={24} className="hidden sm:block text-yellow-600" />
              </div>
              <div className="min-w-0">
                <p className="text-lg sm:text-2xl font-bold">{totalPendingCount}</p>
                <p className="text-xs sm:text-sm text-gray-500 truncate">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-2 sm:p-4 flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-green-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                <Check size={16} className="sm:hidden text-green-600" />
                <Check size={24} className="hidden sm:block text-green-600" />
              </div>
              <div className="min-w-0">
                <p className="text-lg sm:text-2xl font-bold">{approvedCount}</p>
                <p className="text-xs sm:text-sm text-gray-500 truncate">Approved</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-2 sm:p-4 flex items-center gap-2 sm:gap-4">
              <div className="w-8 h-8 sm:w-12 sm:h-12 bg-red-100 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0">
                <X size={16} className="sm:hidden text-red-600" />
                <X size={24} className="hidden sm:block text-red-600" />
              </div>
              <div className="min-w-0">
                <p className="text-lg sm:text-2xl font-bold">{rejectedCount}</p>
                <p className="text-xs sm:text-sm text-gray-500 truncate">Rejected</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Admin Tabs */}
      {isAdmin && (
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit" data-testid="admin-leave-tabs">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'overview' ? 'bg-white text-[#1E2A5E] shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-overview"
          >
            <Users size={14} className="inline mr-1.5 -mt-0.5" />
            Employee Leave Overview
          </button>
          <button
            onClick={() => setActiveTab('requests')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === 'requests' ? 'bg-white text-[#1E2A5E] shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
            data-testid="tab-requests"
          >
            <Clock size={14} className="inline mr-1.5 -mt-0.5" />
            Leave Requests ({totalPendingCount} pending)
          </button>
        </div>
      )}

      {/* Admin: All Employee Leave Overview */}
      {isAdmin && activeTab === 'overview' && (
        <Card data-testid="leave-overview-section">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <Users size={16} className="text-[#1E2A5E]" />
                All Employees Leave Balance
              </h3>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
                  <Input
                    placeholder="Search employee..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8 h-9 w-48 text-sm"
                    data-testid="leave-search-input"
                  />
                </div>
                <Select value={String(balanceYear)} onValueChange={(v) => setBalanceYear(parseInt(v))}>
                  <SelectTrigger className="w-24 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="h-9" onClick={loadAllLeaveBalances} disabled={loadingBalances}>
                  <RefreshCw size={14} className={loadingBalances ? 'animate-spin' : ''} />
                </Button>
              </div>
            </div>

            {loadingBalances ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-[#1E2A5E]" />
              </div>
            ) : filteredBalances.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Users size={32} className="mx-auto mb-2 opacity-40" />
                <p className="text-sm">No monthly employees found</p>
              </div>
            ) : (
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="p-2.5 text-left font-medium text-gray-700">#</th>
                      <th className="p-2.5 text-left font-medium text-gray-700">Employee</th>
                      <th className="p-2.5 text-left font-medium text-gray-700">Role</th>
                      <th className="p-2.5 text-center font-medium text-gray-700">Working Days</th>
                      <th className="p-2.5 text-center font-medium text-gray-700">Accrued</th>
                      <th className="p-2.5 text-center font-medium text-gray-700">Used</th>
                      <th className="p-2.5 text-center font-medium text-gray-700">Pending</th>
                      <th className="p-2.5 text-center font-medium text-gray-700">Available</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredBalances.map((emp, idx) => (
                      <tr key={emp.emp_id} className="hover:bg-gray-50" data-testid={`leave-row-${emp.emp_id}`}>
                        <td className="p-2.5 text-gray-400 text-xs">{idx + 1}</td>
                        <td className="p-2.5">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                              {emp.name?.charAt(0) || '?'}
                            </div>
                            <div>
                              <p className="font-medium text-gray-800 text-sm">{emp.name}</p>
                              <p className="text-xs text-gray-400">{emp.emp_id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-2.5">
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 capitalize">
                            {emp.designation || emp.role}
                          </span>
                        </td>
                        <td className="p-2.5 text-center">
                          <span className="text-sm font-medium text-blue-600">{emp.working_days}</span>
                        </td>
                        <td className="p-2.5 text-center">
                          <span className="text-sm font-medium text-green-600">{emp.total_accrued}</span>
                        </td>
                        <td className="p-2.5 text-center">
                          <span className="text-sm font-medium text-orange-600">{emp.total_used}</span>
                        </td>
                        <td className="p-2.5 text-center">
                          {emp.pending_requests > 0 ? (
                            <span className="text-sm font-bold text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded">{emp.pending_requests}</span>
                          ) : (
                            <span className="text-sm text-gray-400">0</span>
                          )}
                        </td>
                        <td className="p-2.5 text-center">
                          <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                            emp.available > 0 ? 'text-emerald-700 bg-emerald-50' : 
                            emp.available < 0 ? 'text-red-700 bg-red-50' : 'text-gray-500'
                          }`}>
                            {emp.available}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t font-medium text-sm">
                      <td colSpan="3" className="p-2.5 text-gray-600">
                        Total: {filteredBalances.length} employees
                      </td>
                      <td className="p-2.5 text-center text-blue-600">
                        {filteredBalances.reduce((s, e) => s + e.working_days, 0)}
                      </td>
                      <td className="p-2.5 text-center text-green-600">
                        {filteredBalances.reduce((s, e) => s + e.total_accrued, 0)}
                      </td>
                      <td className="p-2.5 text-center text-orange-600">
                        {filteredBalances.reduce((s, e) => s + e.total_used, 0)}
                      </td>
                      <td className="p-2.5 text-center text-yellow-600">
                        {filteredBalances.reduce((s, e) => s + e.pending_requests, 0)}
                      </td>
                      <td className="p-2.5 text-center text-emerald-600">
                        {filteredBalances.reduce((s, e) => s + e.available, 0)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            <p className="text-[10px] text-gray-400 mt-2">
              * Accrual Rule: 1 leave per 24 working days. Only monthly salary employees shown.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Leave Balance Card - For Employee/TL (their own balance) */}
      {!isAdmin && leaveBalance && (
        <Card className="border-2 border-blue-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardContent className="p-3 sm:p-4">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <h3 className="text-base sm:text-lg font-semibold text-gray-800 flex items-center gap-2">
                <CalendarDays size={18} className="text-blue-600" />
                My Leave Balance
              </h3>
              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                <SelectTrigger className="w-20 sm:w-24 h-8 sm:h-10 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
              <div className="text-center p-2 sm:p-3 bg-white rounded-lg shadow-sm">
                <Briefcase size={16} className="mx-auto text-blue-500 mb-1" />
                <p className="text-xl sm:text-2xl font-bold text-blue-600">{leaveBalance.working_days_count}</p>
                <p className="text-[10px] sm:text-xs text-gray-500">Working Days</p>
              </div>
              <div className="text-center p-2 sm:p-3 bg-white rounded-lg shadow-sm">
                <CalendarCheck size={16} className="mx-auto text-green-500 mb-1" />
                <p className="text-xl sm:text-2xl font-bold text-green-600">{leaveBalance.total_leave}</p>
                <p className="text-[10px] sm:text-xs text-gray-500">Total Leaves</p>
                <p className="text-[9px] sm:text-xs text-gray-400">(1 per 24 days)</p>
              </div>
              <div className="text-center p-2 sm:p-3 bg-white rounded-lg shadow-sm">
                <CalendarOff size={16} className="mx-auto text-orange-500 mb-1" />
                <p className="text-xl sm:text-2xl font-bold text-orange-600">{leaveBalance.total_used}</p>
                <p className="text-[10px] sm:text-xs text-gray-500">Used Leaves</p>
              </div>
              <div className="text-center p-2 sm:p-3 bg-white rounded-lg shadow-sm border-2 border-green-200">
                <Calendar size={16} className="mx-auto text-emerald-500 mb-1" />
                <p className="text-xl sm:text-2xl font-bold text-emerald-600">{leaveBalance.total_leave - leaveBalance.total_used}</p>
                <p className="text-[10px] sm:text-xs text-gray-500">Available</p>
              </div>
            </div>
            <p className="text-[10px] sm:text-xs text-gray-500 mt-2 sm:mt-3 text-center">
              Auto Leave Accrual: 1 leave is earned for every 24 working days (excluding IDLE, Week off, Office)
            </p>
          </CardContent>
        </Card>
      )}

      {/* Filter & Bulk Actions - Show when not admin OR admin on requests tab */}
      {(!isAdmin || activeTab === 'requests') && (
      <>
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Requests</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            
            {(isAdmin || isTeamLead) && pendingCount > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={handleSelectAll}>
                  <CheckSquare size={16} className="mr-1" />
                  {selectedIds.length === pendingCount ? 'Deselect' : 'Select All'}
                </Button>
                {selectedIds.length > 0 && (
                  <>
                    <span className="text-sm text-gray-500">{selectedIds.length} selected</span>
                    <Button size="sm" onClick={handleBulkApprove} className="bg-green-600 hover:bg-green-700" disabled={bulkProcessing}>
                      <Check size={16} className="mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="destructive" onClick={handleBulkReject} disabled={bulkProcessing}>
                      <X size={16} className="mr-1" /> Reject
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Leave List */}
      <div className="space-y-4">
        {filteredLeaves.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-gray-500">
              <CalendarOff size={48} className="mx-auto mb-4 opacity-50" />
              <p>No leave requests found</p>
            </CardContent>
          </Card>
        ) : (
          filteredLeaves.map((leave) => (
            <Card key={leave.id} className={`hover:shadow-md transition-shadow ${selectedIds.includes(leave.id) ? 'ring-2 ring-blue-500' : ''}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    {(isAdmin || isTeamLead) && leave.status === 'pending' && (
                      <Checkbox checked={selectedIds.includes(leave.id)} onCheckedChange={() => handleSelect(leave.id)} className="mt-3" />
                    )}
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold">
                      {leave.emp_name?.split(' ').map(n => n[0]).join('') || 'U'}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800">{leave.emp_name}</h3>
                      <p className="text-sm text-gray-500">{leave.type}</p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                        <span className="flex items-center gap-1"><Calendar size={14} /> {leave.from_date} to {leave.to_date}</span>
                        <span className="bg-gray-100 px-2 py-0.5 rounded">{leave.days} day(s)</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-2">{leave.reason}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                      leave.status === 'approved' ? 'bg-green-100 text-green-700' :
                      leave.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {leave.status}
                    </span>
                    {(isAdmin || isTeamLead) && leave.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleApprove(leave.id)} className="bg-green-600 hover:bg-green-700">
                          <Check size={16} />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleReject(leave.id)}>
                          <X size={16} />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      </>
      )}
    </div>
  );
};

export default Leaves;
